import Seo from "../components/Seo";
import { useEffect, useState, useMemo } from "react";
import { Printer, ArrowLeft, BookOpen, CheckSquare, Square, FileText } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import QRWithLogo from "../components/QRWithLogo";
import ComuneCombobox from "../components/ComuneCombobox";
import { Button } from "../components/ui/button";

export default function Bacheca() {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [comune, setComune] = useState("all");
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    const params = comune !== "all" ? { comune } : {};
    api.get("/books", { params }).then(({ data }) => {
      const real = data.filter((b) => !b.is_demo).slice(0, 30);
      setBooks(real);
      setSelected(new Set(real.map((b) => b.id)));
    });
  }, [comune]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allOn = books.length > 0 && selected.size === books.length;
  const toggleAll = () => setSelected(allOn ? new Set() : new Set(books.map((b) => b.id)));
  const selCount = books.filter((b) => selected.has(b.id)).length;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 print-a4">
      <Seo title="Bacheca stampabile dei libri | Libri in Circolo" description="Crea e stampa una bacheca A4 con i libri disponibili nella tua zona e il QR code del catalogo, da affiggere in biblioteca, bar o bacheche di quartiere." path="/bacheca" />
      <div className="flex items-center justify-between gap-3 mb-6 no-print flex-wrap">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2"><ArrowLeft className="w-4 h-4 mr-2" />Indietro</Button>
          <h1 className="text-2xl sm:text-3xl font-serif font-bold tracking-tight">Bacheca stampabile</h1>
          <p className="text-sm text-muted-foreground mt-1">Scegli i libri da includere e stampa un volantino A4 con i loro QR.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" asChild data-testid="bacheca-locandina"><Link to="/locandina"><FileText className="w-4 h-4 mr-2" />Locandina comune</Link></Button>
          <div className="w-44">
            <ComuneCombobox value={comune} onChange={setComune} testId="bacheca-comune" allowAll allLabel="Tutti i comuni" placeholder="Comune" triggerClassName="h-9" />
          </div>
          <Button data-testid="bacheca-print" onClick={() => window.print()} disabled={selCount === 0} className="bg-primary hover:bg-primary/90"><Printer className="w-4 h-4 mr-2" />Stampa</Button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3 no-print">
        <button onClick={toggleAll} data-testid="bacheca-select-all" className="inline-flex items-center gap-2 text-sm font-medium hover:text-primary">
          {allOn ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}{allOn ? "Deseleziona tutti" : "Seleziona tutti"}
        </button>
        <span className="text-xs text-muted-foreground">{selCount} libri selezionati</span>
      </div>

      <div className="rounded-2xl border border-border bg-white text-stone-900 p-6 sm:p-8" data-testid="bacheca-sheet">
        <div className="text-center mb-6">
          <h2 className="font-serif text-2xl font-bold">Libri in Circolo</h2>
          <p className="text-sm text-stone-500">Scambia gratuitamente libri con altri cittadini{comune !== "all" ? ` · ${comune}` : ""}. Inquadra un QR per aprire l'annuncio.</p>
        </div>

        {books.length === 0 ? (
          <div className="text-center py-16 text-stone-400"><BookOpen className="w-8 h-8 mx-auto mb-2" />Nessun libro disponibile.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
            {books.map((b) => {
              const on = selected.has(b.id);
              return (
                <div key={b.id} data-testid={`bacheca-card-${b.id}`}
                  className={`relative flex flex-col items-center text-center border border-stone-200 rounded-lg p-3 transition-opacity ${on ? "" : "opacity-30 print:hidden"}`}>
                  <button onClick={() => toggle(b.id)} data-testid={`bacheca-toggle-${b.id}`}
                    className="absolute top-1.5 right-1.5 no-print text-stone-500 hover:text-primary">
                    {on ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5" />}
                  </button>
                  <QRWithLogo value={`${origin}/libro/${b.id}`} size={96} />
                  <p className="font-serif font-semibold text-sm mt-2 leading-tight line-clamp-2">{b.title}</p>
                  <p className="text-xs text-stone-500 line-clamp-1">{b.author}</p>
                  <p className="text-[11px] text-stone-400 mt-0.5">{b.comune}{b.is_gift ? " · In regalo" : ""}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
